import { observer } from 'mobx-react-lite';
import { createContext, ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import {
    ActionHandler,
    useIntegratedActionBroadcaster,
} from '../action-broadcaster/use-integrated-action-broadcaster';
import { usePrevious } from '../common/hooks/use-previous';
import { useStableEffect } from '../common/hooks/use-stable-effect';
import { useWorkspacePanel } from '../panels/hooks/use-workspace-panel';
import { useEventMetadataStore } from './event-panel-form/event-metadata';
import { useFormHasHadDataSetAtLeastOnceContext } from './event-state/form-has-had-data-set-at-least-once';
import { EventPanelTab } from './tabs/components/event-panel-tabs-config';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';

export type SetActiveEventPanelTab = React.Dispatch<
    React.SetStateAction<EventPanelTab | undefined>
>;

const ActiveEventPanelTabContext = createContext<EventPanelTab | undefined>(undefined);
const SetActiveEventPanelTabContext = createContext<undefined | SetActiveEventPanelTab>(undefined);

export const ActiveEventPanelTabProvider = observer(function ActiveEventPanelTabProvider(props: {
    children: ReactElement;
}) {
    const workspacePanel = useWorkspacePanel<'EVENT'>();
    const [activeEventPanelTab, setActiveEventPanelTab] = useState<EventPanelTab | undefined>(
        undefined
    );
    const workspacePanelUuid = workspacePanel.uuid;
    const eventId = workspacePanel.linkedEntityId;
    const eventMetadata = useEventMetadataStore().state.metadata;

    const formHasHadDataSetAtLeastOnceContext = useFormHasHadDataSetAtLeastOnceContext();

    const [defaultActiveTabHasBeenSet, setDefaultActiveTabHasBeenSet] = useState(false);
    const isScheduledEvent = eventMetadata?.isScheduledEvent;
    const previousIsScheduledEvent = usePrevious(isScheduledEvent);

    const enabledFeatures = useEnabledFeatures();

    /**
     * Wait for the first instance of form data to load before
     * setting the default active tab. We need to wait because
     * the default tab is dependenct on form state
     */
    useStableEffect(function setDefaultActiveTab() {
        if (formHasHadDataSetAtLeastOnceContext) {
            setActiveEventPanelTab(
                workspacePanel.getPanelState('initialTab') ||
                    // If its a new event (no event id)
                    !eventId
                    ? 'ANI_ALI_RAPIDSOS'
                    : eventMetadata?.isScheduledEvent
                    ? undefined
                    : eventMetadata?.isActive
                    ? enabledFeatures.openRelatedDataTabByDefault
                        ? 'RELATED_DATA'
                        : 'CAUTIONS'
                    : // This should refer to the scenario where the event is cleared
                      'EVENT_DETAILS'
            );
            setDefaultActiveTabHasBeenSet(true);
        }
    }, formHasHadDataSetAtLeastOnceContext);

    /**
     * Specifically when an event transitions from `scheduled` to `active`, we set the active tab to cautions
     * We need to make use of `usePrevious` - otherwise this effect
     * gets called when opening any existing event, as their `isScheduledEvent` value
     * transitions from `undefined` to `false`
     */
    useEffect(
        function eventTransitionsFromScheduledToActive() {
            if (previousIsScheduledEvent === true && isScheduledEvent === false) {
                if (enabledFeatures.openRelatedDataTabByDefault) {
                    setActiveEventPanelTab('RELATED_DATA');
                } else {
                    setActiveEventPanelTab('CAUTIONS');
                }
            }
        },
        [previousIsScheduledEvent, isScheduledEvent, enabledFeatures.openRelatedDataTabByDefault]
    );

    const handleActions: ActionHandler<'EVENT_V2'> = useCallback(
        (action) => {
            switch (action.type) {
                case 'SET_EVENT_PANEL_TAB': {
                    if (
                        ('workspacePanelClientUuid' in action.payload &&
                            action.payload.workspacePanelClientUuid === workspacePanelUuid) ||
                        ('eventId' in action.payload && action.payload.eventId === eventId)
                    ) {
                        setActiveEventPanelTab(action.payload.tab);
                        return true;
                    }
                    break;
                }
            }
        },
        [setActiveEventPanelTab, workspacePanelUuid, eventId]
    );

    useIntegratedActionBroadcaster({
        id: 'EVENT_PANEL_TABS',
        windowType: 'EVENT_V2',
        /**
         * Only after the default has been set, do we start listening
         * for requests to change the tab
         *
         * Otherwise, it would be possible for the `default` to override
         * the request
         */
        handler: defaultActiveTabHasBeenSet ? handleActions : undefined,
    });

    return (
        <ActiveEventPanelTabContext.Provider value={activeEventPanelTab}>
            <SetActiveEventPanelTabContext.Provider value={setActiveEventPanelTab}>
                {props.children}
            </SetActiveEventPanelTabContext.Provider>
        </ActiveEventPanelTabContext.Provider>
    );
});

export function useActiveEventPanelTab() {
    return useContext(ActiveEventPanelTabContext);
}

export function useSetActiveEventPanelTab() {
    const value = useContext(SetActiveEventPanelTabContext);
    if (!value)
        throw new Error('Unexpectedly did not find active event panel tab setter on context');
    return value;
}
